import { Component, ErrorInfo, ReactNode, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

import AlertIllustration from "src/assets/illustrations/infoalertIllustrations";
import useLocales from "@hooks/useLocales";
import { logFrontendError } from "src/helpers/frontendErrorLogger";

interface IErrorDetails {
  error: Error;
  info: ErrorInfo;
}

interface IErrorBoundaryFallbackProps {
  errorDetails: IErrorDetails | null;
  onReset: () => void;
}

const ErrorBoundaryFallback: React.FC<IErrorBoundaryFallbackProps> = ({
  errorDetails,
  onReset,
}) => {
  const { translate } = useLocales();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 8,
        px: 3,
      }}
    >
      <AlertIllustration />
      <Typography variant="h5" sx={{ mt: 3 }}>
        {translate("something_went_wrong")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, maxWidth: 420 }}
      >
        {translate("something_went_wrong_description")}
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
        <Button
          onClick={onReset}
          variant="contained"
          startIcon={<RefreshCw size={16} />}
        >
          {translate("try_again")}
        </Button>

        {errorDetails && (
          <Button
            onClick={() => setShowDetails((prev) => !prev)}
            variant="outlined"
            color="inherit"
            startIcon={showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          >
            {translate(showDetails ? "hide_error_details" : "show_error_details")}
          </Button>
        )}
      </Box>

      {errorDetails && showDetails && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            p: 2,
            maxWidth: 720,
            width: "100%",
            textAlign: "left",
            bgcolor: "background.neutral",
          }}
        >
          <Typography
            variant="caption"
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              color: "error.main",
            }}
          >
            {errorDetails.error.toString()}
          </Typography>
          {errorDetails.error.stack && (
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                color: "text.secondary",
              }}
            >
              {errorDetails.error.stack}
            </Typography>
          )}
          {errorDetails.info.componentStack && (
            <Typography
              variant="caption"
              component="pre"
              sx={{
                mt: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                color: "text.secondary",
              }}
            >
              {errorDetails.info.componentStack}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

interface IErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface IErrorBoundaryState {
  hasError: boolean;
  errorDetails: IErrorDetails | null;
}

class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorDetails: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(): Partial<IErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info);
    logFrontendError(error, "error_boundary", info.componentStack);
    this.setState({ errorDetails: { error, info } });
  }

  handleReset() {
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <ErrorBoundaryFallback
          errorDetails={this.state.errorDetails}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
